import type {
  AdminInvoiceReportColumn,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import { convertFormDataToApiQuery } from './Search/hooks/useSearchForm';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from './Search';
import { AdminInvoiceReportsTable } from './Table';
import { useAdminInvoiceReportsFormInitial } from './hooks/useAdminInvoiceReportsFormInitial';
import { useAdminInvoiceReportsHandleSearchQuery } from './hooks/useAdminInvoiceReportsHandleSearchQuery';
import { useAdminInvoiceReportsSearchData } from './hooks/useAdminInvoiceReportsSearchData';
import styles from './List.module.scss';

const cx = classnames.bind(styles);

// Stable empty constant — prevents new array reference on each render
const EMPTY_SELECTED: string[] = [];

const useTableSelection = (data: AdminInvoiceReportColumn[]) => {
  const [selectedRowKeys, setSelectedRowKeys] =
    useState<string[]>(EMPTY_SELECTED);

  const selectableItems = useMemo(
    () => data.filter((item) => item.delete_action.allowed),
    [data]
  );

  const isAllSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedRowKeys.includes(item.id));

  const isAllDisabled = selectableItems.length === 0;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedRowKeys(checked ? selectableItems.map((item) => item.id) : []);
    },
    [selectableItems]
  );

  const handleSelectRow = useCallback(
    (record: AdminInvoiceReportColumn, checked: boolean) => {
      setSelectedRowKeys((prev) =>
        checked ? [...prev, record.id] : prev.filter((id) => id !== record.id)
      );
    },
    []
  );

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    isAllSelected,
    isAllDisabled,
    handleSelectAll,
    handleSelectRow,
  };
};

export const List: FC = memo(() => {
  const defaultValues = useAdminInvoiceReportsFormInitial();
  const initialQuery = useMemo(
    () => convertFormDataToApiQuery(defaultValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const { fetchArgs, searchQuery, sortOrders, onSearch, onMultiColumnSort } =
    useAdminInvoiceReportsHandleSearchQuery(initialQuery);

  const { data, pagination, refetch, isFetching } =
    useAdminInvoiceReportsSearchData(fetchArgs);

  const onHandleSearch = useCallback(
    (query: AdminInvoiceReportsSearchQuery) => {
      onSearch(query, refetch);
    },
    [onSearch, refetch]
  );

  const {
    selectedRowKeys,
    setSelectedRowKeys,
    isAllSelected,
    isAllDisabled,
    handleSelectAll,
    handleSelectRow,
  } = useTableSelection(data);

  // Clear selection when a new fetch starts
  const selectedCount = selectedRowKeys.length;

  useEffect(() => {
    if (isFetching && selectedCount > 0) {
      setSelectedRowKeys([]);
    }
  }, [isFetching, selectedCount, setSelectedRowKeys]);

  const isSearchEmpty = useMemo(
    () => data.length === 0 && Object.keys(fetchArgs.query ?? {}).length > 0,
    [data.length, fetchArgs.query]
  );

  // Placeholder callbacks — cancel/delete modals to be implemented in later stories
  const handleCancel = useCallback(() => {
    // TODO: open cancel modal (NA-XXXX)
  }, []);

  const handleDelete = useCallback(() => {
    // TODO: open delete modal (NA-XXXX)
  }, []);

  return (
    <div className={cx('container')}>
      <Search
        defaultValues={defaultValues}
        onSearch={onHandleSearch}
        searchQuery={searchQuery}
        isLoading={isFetching}
      />
      <AdminInvoiceReportsTable
        data={data}
        pagination={pagination}
        isLoading={isFetching}
        isSearchEmpty={isSearchEmpty}
        sortOrders={sortOrders}
        onMultiColumnSort={onMultiColumnSort}
        onSearch={onSearch}
        selectedRowKeys={selectedRowKeys}
        handleSelectAll={handleSelectAll}
        handleSelectRow={handleSelectRow}
        isAllSelected={isAllSelected}
        isAllDisabled={isAllDisabled || isFetching}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
    </div>
  );
});

List.displayName = 'List';
